import { Alert } from 'src/design/atoms/Alert';
import { Group } from 'src/design/atoms/Group';
import { Stack } from 'src/design/atoms/Stack';
import { Textarea } from 'src/design/atoms/Textarea';
import { TextInput } from 'src/design/atoms/TextInput';
import { BaseModal } from 'src/design/molecules/BaseModal';
import { useFormik } from 'formik';
import React from 'react';
import { MilestonePhaseDto } from 'src/api/FiretrolApiClient';
import {
  useCreateMilestonePhase,
  useUpdateMilestonePhase,
} from 'src/api/queries/milestones/useMilestonePhaseMutations';
import { ModalButton } from 'src/design/molecules/ModalButton';
import { SecondaryButton } from 'src/design/atoms/SecondaryButton';
import * as Yup from 'yup';
import { getErrorMessage } from 'src/utils';

const validationSchema = Yup.object().shape({
  title: Yup.string().required().min(1, 'Title Required').label('Title'),
});

export interface IMilestonePhaseDialogProps {
  isOpen: boolean;
  onClose: () => void;
  jobId?: number;
  onCreateComplete?: () => void;
  onUpdateComplete?: () => void;
  templateId?: string;
  data?: MilestonePhaseDto;
}

/** @registry Modal for creating/editing a milestone phase, on both jobs and templates. */
export const MilestonePhaseDialog: React.FC<IMilestonePhaseDialogProps> = ({
  isOpen,
  onClose,
  jobId,
  templateId,
  onCreateComplete,
  onUpdateComplete,
  data,
}) => {
  const [error, setError] = React.useState<string | null>(null);
  const isEditMode = !!data;
  const scope = { jobId, templateId };
  const createPhase = useCreateMilestonePhase();
  const updatePhase = useUpdateMilestonePhase();

  const formik = useFormik({
    initialValues: {
      title: '',
      description: '',
      templateId: templateId || null,
    },
    validationSchema,
    validateOnMount: false,
    onSubmit: async (values) => {
      setError(null);
      try {
        if (isEditMode && data) {
          await updatePhase.mutateAsync({
            scope,
            phaseId: data.id,
            request: values,
          });
          onUpdateComplete?.();
        } else {
          await createPhase.mutateAsync({ scope, request: values });
          onCreateComplete?.();
        }
        closeAndReset();
      } catch (err) {
        setError(getErrorMessage(err));
      }
    },
  });

  React.useEffect(() => {
    if (data) {
      formik.setValues({
        title: data.title,
        description: data.description,
        templateId: templateId || null,
      });
    } else {
      formik.setValues({
        title: '',
        description: '',
        templateId: templateId || null,
      });
    }
  }, [data, templateId]);

  const closeAndReset = () => {
    formik.resetForm();
    setError(null);
    onClose();
  };

  return (
    <BaseModal
      opened={isOpen}
      onClose={onClose}
      title={isEditMode ? 'Edit Phase' : 'New Phase'}
      size="md"
      centered
    >
      <Stack>
        {error && (
          <Alert title="Error" color="red" mb="md">
            {error}
          </Alert>
        )}

        <TextInput
          label="Title"
          placeholder="Enter phase title"
          value={formik.values.title}
          onChange={(e) => formik.setFieldValue('title', e.target.value)}
          error={formik.touched.title && formik.errors.title}
          required
        />

        <Textarea
          label="Description"
          placeholder="Enter phase description"
          value={formik.values.description}
          onChange={(e) => formik.setFieldValue('description', e.target.value)}
          error={formik.touched.description && formik.errors.description}
          autosize
          minRows={3}
        />

        <Group justify="flex-end">
          <SecondaryButton onClick={closeAndReset}>Cancel</SecondaryButton>
          <ModalButton
            loading={formik.isSubmitting}
            onClick={() => formik.handleSubmit()}
          >
            {isEditMode ? 'Update' : 'Save'}
          </ModalButton>
        </Group>
      </Stack>
    </BaseModal>
  );
};
